import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PodcastAdmin = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false); // Initialize to false so login button isn't spinning initially
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [showPassword, setShowPassword] = useState(false); // Add state for password visibility
  
  // Fetch podcast episodes from API
  useEffect(() => {
    // Define fetchEpisodes inside useEffect to avoid dependency issues
    const fetchEpisodes = async () => {
      if (!authenticated) return; // Don't fetch if not authenticated
      
      try {
        setLoading(true); // Only set loading when actually fetching
        const response = await fetch('/api/episodes');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setEpisodes(data);
      } catch (error) {
        console.error('Error fetching podcast episodes:', error);
        setError('Failed to load podcast episodes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch episodes if authenticated
    if (authenticated) {
      fetchEpisodes();
    }
  }, [authenticated]); // Only authenticated as dependency now

  const handleAuthenticate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Trim the API key to remove any accidental whitespace
    const trimmedKey = apiKey.trim();
    if (trimmedKey !== apiKey) {
      console.log('API key had whitespace, trimmed it');
      setApiKey(trimmedKey);
    }
    
    console.log('Attempting authentication with key:', trimmedKey);
    
    try {
      // Verify the API key by trying to make a health check request with it
      // For testing purposes, try the health check first without auth
      console.log('Testing server health before auth...');
      try {
        const healthResponse = await fetch('/api/episodes/health');
        console.log('Health check status:', healthResponse.status);
      } catch (healthError) {
        console.error('Health check failed:', healthError);
      }
      
      // Now try with auth
      const response = await fetch('/api/episodes/health', {
        method: 'GET',
        headers: {
          'X-API-Key': trimmedKey
        }
      });
      
      console.log('Auth response status:', response.status);
      
      let data;
      try {
        data = await response.json();
        console.log('Auth response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        data = { message: 'Server response was not valid JSON' };
      }
      
      if (response.ok) {
        console.log('Authentication successful');
        setAuthenticated(true);
        setError(null);
      } else {
        console.error('Authentication failed with status:', response.status);
        setError(`Invalid API key. Server responded with: ${data?.message || response.statusText}`);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(`Authentication failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setApiKey('');
  };

  const handleSaveEpisodes = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Make sure we have the API key stored from the login process
      if (!apiKey) {
        throw new Error("API key is missing. Please log out and log back in.");
      }
      
      // Validate episodes before sending to server
      const invalidEpisodes = episodes.filter(episode => {
        return !episode.id || !episode.title || !episode.description || 
               !episode.youtubeId || !episode.youtubeUrl || !episode.episodeNumber;
      });
      
      if (invalidEpisodes.length > 0) {
        throw new Error(`${invalidEpisodes.length} episode(s) have missing required fields. Please complete all episodes before saving.`);
      }
      
      const response = await fetch('/api/episodes', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey.trim()
        },
        body: JSON.stringify(episodes)
      });
      
      if (!response.ok) {
        // Try to get more detailed error message from the response
        try {
          const errorData = await response.json();
          throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || response.statusText}`);
        } catch (jsonError) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      }
      
      const result = await response.json(); // Process the response
      
      // Use the message from the server or default message
      setSuccess(result.message || 'Podcast episodes saved successfully!');
      
      // If there was an issue with the DigitalOcean upload, show a warning instead of success
      if (result.doSpacesUploadSuccess === false) {
        setError('Warning: Episodes saved locally but failed to upload to DigitalOcean Spaces. Changes may not be visible on the live site until this is resolved, please let Nick know you saw this error.');
      }
    } catch (error) {
      console.error('Error saving podcast episodes:', error);
      setError(`Failed to save podcast episodes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEpisode = () => {
    const newEpisode = {
      id: episodes.length > 0 ? Math.max(...episodes.map(ep => ep.id)) + 1 : 1,
      title: '',
      description: '',
      youtubeId: '',
      youtubeUrl: '',
      episodeNumber: `${episodes.length + 1}`,
      seriesName: 'RPsych Radio',
      tags: []
    };
    
    setEditingEpisode(newEpisode);
    setTagsInput(''); // Clear tags input for a new episode
  };

  const handleEditEpisode = (episode) => {
    // Create a deep copy of the episode to avoid reference issues
    const episodeCopy = JSON.parse(JSON.stringify(episode));
    setEditingEpisode(episodeCopy);
    
    // Set the tags input separately
    if (episode.tags && Array.isArray(episode.tags)) {
      setTagsInput(episode.tags.join(', '));
    } else {
      setTagsInput('');
    }
  };

  const handleSaveEpisode = () => {
    if (!editingEpisode.title || !editingEpisode.description || !editingEpisode.youtubeId) {
      setError('Please fill out all required fields');
      return;
    }
    
    // Process the tags input when saving
    const tagsArray = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    // Create the final episode object with processed tags
    const finalEpisode = {
      ...editingEpisode,
      tags: tagsArray,
      // Always ensure youtubeUrl is set if youtubeId is present
      youtubeUrl: editingEpisode.youtubeUrl || `https://www.youtube.com/watch?v=${editingEpisode.youtubeId}`
    };
    
    const isNew = !episodes.some(ep => ep.id === finalEpisode.id);
    let updatedEpisodes;
    
    if (isNew) {
      updatedEpisodes = [...episodes, finalEpisode];
    } else {
      updatedEpisodes = episodes.map(ep => 
        ep.id === finalEpisode.id ? finalEpisode : ep
      );
    }
    
    setEpisodes(updatedEpisodes);
    setEditingEpisode(null);
    setTagsInput('');
    setSuccess('Episode saved! Remember to save all changes to update the website.');
  };

  const handleCancelEdit = () => {
    setEditingEpisode(null);
    setTagsInput('');
  };

  const handleDeleteEpisode = (id) => {
    if (window.confirm('Are you sure you want to delete this episode?')) {
      const updatedEpisodes = episodes.filter(ep => ep.id !== id);
      setEpisodes(updatedEpisodes);
      setSuccess('Episode deleted! Remember to save all changes to update the website.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingEpisode({
      ...editingEpisode,
      [name]: value
    });
  };

  // Completely separate tags input from episode state
  const [tagsInput, setTagsInput] = useState('');
  
  // Set initial tags input when episode changes
  useEffect(() => {
    if (editingEpisode && editingEpisode.tags) {
      setTagsInput(editingEpisode.tags.join(', '));
    } else {
      setTagsInput('');
    }
  }, [editingEpisode]);
  
  // Just update the input field, don't parse it yet
  const handleTagsChange = (e) => {
    setTagsInput(e.target.value);
  };

  return (
    <>
      <Helmet>
        <title>Podcast Admin | Redemption Psychiatry</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css" />
      </Helmet>

      <div className="wrap">
        <Header />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div id="content" role="main" className="admin-page">
            <div id="interior-header-container" className="container-fluid">
              <div className="container">
                <h1>Podcast Episode Administration</h1>
              </div>
            </div>

            <div className="container py-5">
              {!authenticated ? (
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="card shadow">
                      <div className="card-body p-4">
                        <h2 className="card-title mb-4 text-center">Admin Login</h2>
                        <form onSubmit={handleAuthenticate}>
                          <div className="mb-3">
                            <label htmlFor="apiKey" className="form-label">Admin API Key</label>
                            <div className="position-relative">
                              <input 
                                type={showPassword ? "text" : "password"}
                                className="form-control" 
                                id="apiKey" 
                                value={apiKey} 
                                onChange={(e) => setApiKey(e.target.value)}
                                required
                              />
                              <button 
                                className="btn btn-link position-absolute"
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ 
                                  top: '0px', 
                                  right: '0px', 
                                  height: '100%', 
                                  width: '40px',
                                  border: 'none',
                                  background: 'transparent',
                                  zIndex: 10
                                }}
                              >
                                <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"}></i>
                              </button>
                            </div>
                          </div>
                          <div className="d-grid">
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Verifying...
                                </>
                              ) : 'Login'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="admin-panel">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Manage Podcast Episodes</h2>
                    <div>
                      <button 
                        className="btn btn-outline-secondary me-2" 
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                      <button 
                        className="btn btn-primary"
                        onClick={handleSaveEpisodes}
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save All Changes'}
                      </button>
                    </div>
                  </div>
                  
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                      <button 
                        type="button" 
                        className="btn-close float-end" 
                        onClick={() => setError(null)}
                        aria-label="Close"
                      ></button>
                    </div>
                  )}
                  
                  {success && (
                    <div className="alert alert-success" role="alert">
                      {success}
                      <button 
                        type="button" 
                        className="btn-close float-end" 
                        onClick={() => setSuccess(null)}
                        aria-label="Close"
                      ></button>
                    </div>
                  )}

                  {loading && !editingEpisode && (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Loading episodes...</p>
                    </div>
                  )}

                  {editingEpisode ? (
                    <div className="card shadow mb-4">
                      <div className="card-header bg-light">
                        <h3 className="card-title mb-0">
                          {editingEpisode.id ? `Edit Episode #${editingEpisode.episodeNumber}` : 'Add New Episode'}
                        </h3>
                      </div>
                      <div className="card-body">
                        <form>
                          <div className="row mb-3">
                            <div className="col-md-8">
                              <label htmlFor="title" className="form-label">Title *</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="title" 
                                name="title"
                                value={editingEpisode.title} 
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-md-4">
                              <label htmlFor="episodeNumber" className="form-label">Episode #</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="episodeNumber" 
                                name="episodeNumber"
                                value={editingEpisode.episodeNumber} 
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <label htmlFor="description" className="form-label">Description *</label>
                            <textarea 
                              className="form-control" 
                              id="description" 
                              name="description"
                              rows="4" 
                              value={editingEpisode.description} 
                              onChange={handleInputChange}
                              required
                            ></textarea>
                          </div>
                          
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label htmlFor="youtubeId" className="form-label">YouTube Video ID *</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="youtubeId" 
                                name="youtubeId"
                                value={editingEpisode.youtubeId} 
                                onChange={handleInputChange}
                                required
                              />
                              <small className="form-text text-muted">
                                The part after v= in the URL (e.g., for https://www.youtube.com/watch?v=abc123, use "abc123")
                              </small>
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="youtubeUrl" className="form-label">YouTube URL *</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="youtubeUrl" 
                                name="youtubeUrl"
                                value={editingEpisode.youtubeUrl || `https://www.youtube.com/watch?v=${editingEpisode.youtubeId}`} 
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label htmlFor="seriesName" className="form-label">Series Name</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="seriesName" 
                                name="seriesName"
                                value={editingEpisode.seriesName} 
                                onChange={handleInputChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="tagInput" className="form-label">Tags (comma-separated)</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="tagInput" 
                                name="tagInput"
                                value={tagsInput} 
                                onChange={handleTagsChange}
                                placeholder="e.g., tag1, tag2, tag3"
                              />
                              <small className="form-text text-muted">
                                Separate tags with commas. You can include spaces in tags if needed.
                              </small>
                              
                              {/* Preview of how the tags will be processed */}
                              {tagsInput && (
                                <div className="mt-2">
                                  <small className="text-muted">Tags preview:</small>
                                  <div className="d-flex flex-wrap gap-1 mt-1">
                                    {tagsInput.split(',')
                                      .map(tag => tag.trim())
                                      .filter(tag => tag.length > 0)
                                      .map((tag, index) => (
                                        <span key={index} className="badge bg-light text-dark border">
                                          {tag}
                                        </span>
                                      ))
                                    }
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="d-flex justify-content-end mt-4">
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary me-2"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-primary"
                              onClick={handleSaveEpisode}
                            >
                              Save Episode
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button 
                        className="btn btn-success mb-4"
                        onClick={handleAddEpisode}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Episode
                      </button>

                      {episodes.length === 0 && !loading ? (
                        <div className="alert alert-info">
                          No episodes found. Click "Add New Episode" to create your first podcast episode.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>ID</th>
                                <th>Title</th>
                                <th>Episode #</th>
                                <th>Series</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {episodes.map(episode => (
                                <tr key={episode.id}>
                                  <td>{episode.id}</td>
                                  <td>{episode.title}</td>
                                  <td>{episode.episodeNumber}</td>
                                  <td>{episode.seriesName}</td>
                                  <td>
                                    <button 
                                      className="btn btn-sm btn-outline-primary me-2"
                                      onClick={() => handleEditEpisode(episode)}
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteEpisode(episode.id)}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="push"></div>
        </motion.div>
        <Footer />
      </div>
    </>
  );
};

export default PodcastAdmin;